import { useMemo } from 'react';

export interface PieInputItem<T = unknown> {
  label: string;
  amount: number;
  original?: T;
  color?: string;
}

export interface PieGroupItem<T = unknown> {
  label: string;
  amount: number;
  percentage: number;
  original?: T;
  color: string;
}

export interface GroupedOther<T = unknown> {
  isOtherGroup: true;
  label: string;
  amount: number;
  percentage: number;
  color: string;
  subItems: PieGroupItem<T>[];
}

export type GroupedItem<T = unknown> = PieGroupItem<T> | GroupedOther<T>;

export interface UsePieGroupingResult<T = unknown> {
  total: number;
  grouped: GroupedItem<T>[];
  hasOtherGroup: boolean;
}

export interface PieGroupingOptions {
  /** Labels that stay on the pie even when they are under the Other cutoff. */
  keepVisibleLabels?: readonly string[];
}

export function getCollapsedPieItems<T = unknown>(
  grouped: GroupedItem<T>[],
): PieGroupItem<T>[] {
  const nonOther = grouped.filter(
    (g): g is PieGroupItem<T> => !('isOtherGroup' in g),
  );
  const otherGroup = grouped.find(
    (g): g is GroupedOther<T> => 'isOtherGroup' in g,
  );

  if (!otherGroup) {
    return nonOther;
  }

  return [
    ...nonOther,
    {
      label: otherGroup.label,
      amount: otherGroup.amount,
      percentage: otherGroup.percentage,
      color: otherGroup.color,
    },
  ];
}

/**
 * Groups pie items by percentage threshold, creating an "Other" group containing
 * all items whose percentage is below the threshold. Colors are assigned using
 * the provided resolver when not present on the item.
 */
export function usePieGrouping<T = unknown>(
  items: PieInputItem<T>[],
  thresholdPercent: number,
  resolveColor: (index: number) => string,
  options?: PieGroupingOptions,
): UsePieGroupingResult<T> {
  const keepVisibleLabels = options?.keepVisibleLabels;
  return useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const total = safeItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const keepVisible = new Set(
      (keepVisibleLabels ?? []).map((label) => label.trim().toLowerCase()),
    );

    const computed = safeItems
      .map<PieGroupItem<T>>((item) => {
        const percentage = total > 0 ? (item.amount / total) * 100 : 0;
        return {
          label: item.label,
          amount: item.amount,
          percentage,
          original: item.original,
          color: item.color ?? '',
        };
      })
      .sort((a, b) => b.percentage - a.percentage);

    const mainItems: PieGroupItem<T>[] = [];
    const smallItems: PieGroupItem<T>[] = [];

    computed.forEach((item) => {
      const staysVisible = keepVisible.has(item.label.trim().toLowerCase());
      if (item.percentage >= thresholdPercent || staysVisible) {
        mainItems.push(item);
      } else {
        smallItems.push(item);
      }
    });

    // Assign colors for main items if missing
    const coloredMain = mainItems.map((item, index) => ({
      ...item,
      color: item.color || resolveColor(index),
    }));

    let grouped: GroupedItem<T>[] = [...coloredMain];
    let hasOtherGroup = false;

    if (smallItems.length > 0) {
      hasOtherGroup = true;
      const otherAmount = smallItems.reduce((sum, i) => sum + i.amount, 0);
      const otherPercentage = total > 0 ? (otherAmount / total) * 100 : 0;

      /*Assign colors to sub-items after the main items palette
      This is to ensure that the Pie Chart legend category colors match the bar chart legend colors 
      since the "Expanded Other" legend item takes up a color in the colors array palette */
      const otherColor = resolveColor(coloredMain.length);
      const coloredSub = smallItems.map((item, idx) => {
        const colorIndex =
          idx === 0 ? coloredMain.length : coloredMain.length + idx;
        return {
          ...item,
          color: item.color || resolveColor(colorIndex),
        };
      });

      const other: GroupedOther<T> = {
        isOtherGroup: true,
        label: 'Other',
        amount: otherAmount,
        percentage: otherPercentage,
        color: otherColor,
        subItems: coloredSub.sort((a, b) => b.amount - a.amount),
      };
      grouped = [...coloredMain, other];
    }

    return {
      total,
      grouped,
      hasOtherGroup,
    };
  }, [items, thresholdPercent, resolveColor, keepVisibleLabels]);
}
